import { useEffect } from 'react';
import { useLocation } from 'wouter';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from './use-toast';
import { getRoleDashboardPath } from '@/lib/auth';

export function useGoogleAuth() {
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const googleAuth = urlParams.get('google_auth');
    
    if (googleAuth === 'success') {
      toast({
        title: "Login Successful",
        description: "Welcome to Servify LaunchPad!",
      });
      
      // Longer delay to ensure session cookie is set before refetching
      // This prevents race conditions where the query runs before the cookie is available
      // Cross-domain cookies can take longer to be available in the browser
      const attemptAuth = (attemptNumber: number, delay: number) => {
        setTimeout(() => {
          // Invalidate and refetch the auth query to update the authentication state
          queryClient.invalidateQueries({ queryKey: ['/auth/me'] });
          
          // Wait for user data to be fetched, then redirect to role-specific dashboard
          queryClient.refetchQueries({ queryKey: ['/auth/me'] }).then(() => {
            // Get the latest user data
            const userData = queryClient.getQueryData(['/auth/me']) as any;
            
            // Check if we actually got user data
            if (!userData || !userData.user) {
              console.warn(`Attempt ${attemptNumber}: No user data received, retrying...`);
              if (attemptNumber < 3) {
                // Retry with exponential backoff
                attemptAuth(attemptNumber + 1, delay * 2);
                return;
              } else {
                console.error('Max retries reached, redirecting to root');
                window.history.replaceState({}, document.title, '/');
                setLocation('/');
                return;
              }
            }
            
            const userRole = userData.user.role;
            const dashboardPath = userRole ? getRoleDashboardPath(userRole) : '/';
            
            // Clear the URL parameters and redirect to the appropriate dashboard
            // Use replaceState to update URL without query params, then navigate
            window.history.replaceState({}, document.title, dashboardPath);
            
            // Redirect to the appropriate dashboard
            setLocation(dashboardPath);
          }).catch((error) => {
            console.error(`Attempt ${attemptNumber}: Failed to fetch user after SSO login:`, error);
            // Retry with exponential backoff (up to 3 attempts)
            if (attemptNumber < 3) {
              attemptAuth(attemptNumber + 1, delay * 2);
            } else {
              // Max retries reached, clear URL and redirect to root
              console.error('Max retries reached after SSO login, redirecting to root');
              window.history.replaceState({}, document.title, '/');
              setLocation('/');
            }
          });
        }, delay);
      };
      
      // Start with a 300ms delay to allow cookie to be set
      attemptAuth(1, 300);
    } else if (googleAuth === 'error') {
      // Clear the URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);
      
      toast({
        title: "Google Login Failed",
        description: "There was an error with Google authentication. Please try again.",
        variant: "destructive",
      });
    }
  }, [queryClient, setLocation, toast]);
}
